import { Component, OnDestroy, OnInit } from '@angular/core';
import { HeadToHeadQueryContext } from '../head-to-head-query-context';
import { AlertService } from '../../shared/alert/alert.service';
import { JhiEventManager } from '../../shared/core-utils';
import { HeadToHeadQueryService } from '../head-to-head-query.service';
import { SubscriptionsManager } from '../../shared/util/subscriptions-manager';
import { ActivatedRoute, Router } from '@angular/router';
import { GermplasmPairTraitModel } from '../model/germplasm-pair-trait.model';
import { finalize } from 'rxjs/internal/operators/finalize';
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { SORT_PREDICATE_NONE } from '../../germplasm-manager/germplasm-search-resolve-paging-params';
import { formatErrorList } from '../../shared/alert/format-error-list';
import { GermplasmPairResultModel } from '../model/germplasm-pair-result.model';
import { Environment, VariableResultsRequestModel } from '../model/variable-results-request.model';
import { VIEW_NAMES_PERMISSIONS } from '../../shared/auth/permissions';
import { MAX_NAME_DISPLAY_SIZE } from '../../app.constants';
import { GermplasmDetailsPopupService } from '../../germplasm-details/germplasm-details-popup.service';
import { ColumnCategory, ColumnModel } from '../../shared/query/model/column.model';

@Component({
    selector: 'jhi-variable-results',
    templateUrl: './variable-results-tab.component.html'
})
export class VariableResultsTabComponent implements OnInit, OnDestroy {
    variable: GermplasmPairTraitModel;
    itemsPerPage: number = 20;
    page: number;
    predicate: any;
    isAscending: boolean;
    isLoading: boolean;
    totalItems: number;
    previousPage: any;
    results: GermplasmPairResultModel[] = [];
    originalResults: GermplasmPairResultModel[] = [];
    pagedData: GermplasmPairResultModel[] = [];
    lastClickIndex: any;
    varResultsSort = VariableResultsSortEnum;
    resultsHiddenColumns = {};
    VIEW_NAMES_PERMISSIONS = [...VIEW_NAMES_PERMISSIONS];

    protected readonly staticColumns = [
        new ColumnModel(-1, 'head-to-head-query.column.header.test.entry', ColumnCategory.STATIC, true, null, true),
        new ColumnModel(-2, 'head-to-head-query.column.header.standard.entry', ColumnCategory.STATIC, true, null, true),
        new ColumnModel(-3, 'head-to-head-query.column.header.no.env', ColumnCategory.STATIC, true, null, true),
        new ColumnModel(-4, 'head-to-head-query.column.header.no.sup', ColumnCategory.STATIC, true, null, true),
        new ColumnModel(-5, 'head-to-head-query.column.header.mean.test', ColumnCategory.STATIC, true, null, true),
        new ColumnModel(-6, 'head-to-head-query.column.header.mean.std', ColumnCategory.STATIC, true, null, true),
        new ColumnModel(-7, 'head-to-head-query.column.header.pval', ColumnCategory.STATIC, true, null, true),
        new ColumnModel(-8, 'head-to-head-query.column.header.mean.diff', ColumnCategory.STATIC, true, null, true)
    ];

    private subscriptionsManager: SubscriptionsManager = new SubscriptionsManager();

    constructor(public queryContext: HeadToHeadQueryContext,
                public alertService: AlertService,
                private eventManager: JhiEventManager,
                private headToHeadQueryService: HeadToHeadQueryService,
                private activatedRoute: ActivatedRoute,
                private router: Router,
                protected germplasmDetailsPopupService: GermplasmDetailsPopupService) {
    }

    ngOnInit(): void {
        this.activatedRoute.params.subscribe(params => {
            this.variable = this.queryContext.traits.find((trait) => trait.variableId === parseInt(params['variableId'], 10));
            this.loadAll();
        });
        this.subscriptionsManager.add = this.eventManager.subscribe('reset-results', (event) => {
            this.results = [];
            this.originalResults = [];
            this.page = 1;
            this.loadAll();
        });
    }

    ngOnDestroy(): void {
        this.subscriptionsManager.unsubscribeAll();
    }

    onClearSort($event) {
        $event.preventDefault();
        this.predicate = SORT_PREDICATE_NONE;
        this.isAscending = true;
        this.results = [...this.originalResults];
        this.updatePagedData();
    }

    loadAll() {
        this.isLoading = true;
        if (!this.results || this.results.length === 0) {
            this.predicate = SORT_PREDICATE_NONE;
            const variableResultsRequest = new VariableResultsRequestModel();
            variableResultsRequest.germplasmPairs = this.queryContext.germplasmPairs;
            variableResultsRequest.traits = this.queryContext.traits;
            variableResultsRequest.environments = Array.from(this.queryContext.environments).map(env => new Environment(env.id, env.weight));
            this.isLoading = true;
            this.subscriptionsManager.add = this.headToHeadQueryService.getGermplasmPairResults(variableResultsRequest).pipe(finalize(() => {
                this.isLoading = false;
            })).subscribe(
                (res: HttpResponse<GermplasmPairResultModel[]>) => this.onSuccess(res)
            );
        } else {
            this.updatePagedData();
        }
    }

    onSuccess(results) {
        this.results = results.body;
        this.originalResults = [...this.results];
        this.totalItems = this.results.length;
        this.page = 1;
        this.previousPage = 1;
        this.updatePagedData();
    }

    updatePagedData() {
        if (this.results && this.results.length > 0) {
            // Calculate starting and ending indices for the current page
            const startIndex = (this.page - 1) * this.itemsPerPage;
            const endIndex = Math.min(Number(startIndex) + Number(this.itemsPerPage), this.totalItems);

            // Slice the data to get the current page data
            this.pagedData = this.results.slice(startIndex, endIndex);
        } else {
            this.pagedData = [];
            this.totalItems = 0;
        }
        this.isLoading = false;
    }

    trackId(index: number, item: any) {
        return item.testGID + '_' + item.stdGID;
    }

    getId(result: GermplasmPairResultModel) {
        return result.testGID + '_' + result.standardGID;
    }

    loadPage(page: number) {
        if (page !== this.previousPage) {
            this.previousPage = page;
            this.updatePagedData();
        }
    }

    sort() {
        this.results = [...this.originalResults];
        if (this.predicate === this.varResultsSort.TEST_ENTRY) {
            this.results.sort((a, b) => {
                if (this.isAscending) {
                    return a.testDesignation > b.testDesignation ? 1 : -1;
                } else {
                    return a.testDesignation < b.testDesignation ? 1 : -1;
                }
            });
        } else if (this.predicate === this.varResultsSort.STD_ENTRY) {
            this.results.sort((a, b) => {
                if (this.isAscending) {
                    return a.standardDesignation > b.standardDesignation ? 1 : -1;
                } else {
                    return a.standardDesignation < b.standardDesignation ? 1 : -1;
                }
            });
        } else if (this.predicate === this.varResultsSort.NO_ENV) {
            this.results.sort((a, b) => {
                if (this.isAscending) {
                    return Number(a.headToHeadResultMap[this.variable.variableId].noOfEnv) > Number(b.headToHeadResultMap[this.variable.variableId].noOfEnv) ? 1 : -1;
                } else {
                    return Number(a.headToHeadResultMap[this.variable.variableId].noOfEnv) < Number(b.headToHeadResultMap[this.variable.variableId].noOfEnv) ? 1 : -1;
                }
            });
        } else if (this.predicate === this.varResultsSort.NO_SUP) {
            this.results.sort((a, b) => {
                if (this.isAscending) {
                    return Number(a.headToHeadResultMap[this.variable.variableId].noSUP) > Number(b.headToHeadResultMap[this.variable.variableId].noSUP) ? 1 : -1;
                } else {
                    return Number(a.headToHeadResultMap[this.variable.variableId].noSUP) < Number(b.headToHeadResultMap[this.variable.variableId].noSUP) ? 1 : -1;
                }
            });
        } else if (this.predicate === this.varResultsSort.MEAN_TEST) {
            this.results.sort((a, b) => {
                if (this.isAscending) {
                    return Number(a.headToHeadResultMap[this.variable.variableId].meanTest) > Number(b.headToHeadResultMap[this.variable.variableId].meanTest) ? 1 : -1;
                } else {
                    return Number(a.headToHeadResultMap[this.variable.variableId].meanTest) < Number(b.headToHeadResultMap[this.variable.variableId].meanTest) ? 1 : -1;
                }
            });
        } else if (this.predicate === this.varResultsSort.MEAN_STD) {
            this.results.sort((a, b) => {
                if (this.isAscending) {
                    return Number(a.headToHeadResultMap[this.variable.variableId].meanSTD) > Number(b.headToHeadResultMap[this.variable.variableId].meanSTD) ? 1 : -1;
                } else {
                    return Number(a.headToHeadResultMap[this.variable.variableId].meanSTD) < Number(b.headToHeadResultMap[this.variable.variableId].meanSTD) ? 1 : -1;
                }
            });
        } else if (this.predicate === this.varResultsSort.PVAL) {
            this.results.sort((a, b) => {
                if (this.isAscending) {
                    return Number(a.headToHeadResultMap[this.variable.variableId].pval) > Number(b.headToHeadResultMap[this.variable.variableId].pval) ? 1 : -1;
                } else {
                    return Number(a.headToHeadResultMap[this.variable.variableId].pval) < Number(b.headToHeadResultMap[this.variable.variableId].pval) ? 1 : -1;
                }
            });
        } else if (this.predicate === this.varResultsSort.MEAN_DIFF) {
            this.results.sort((a, b) => {
                if (this.isAscending) {
                    return Number(a.headToHeadResultMap[this.variable.variableId].meanDiff) > Number(b.headToHeadResultMap[this.variable.variableId].meanDiff) ? 1 : -1;
                } else {
                    return Number(a.headToHeadResultMap[this.variable.variableId].meanDiff) < Number(b.headToHeadResultMap[this.variable.variableId].meanDiff) ? 1 : -1;
                }
            });
        }
        this.updatePagedData();
    }
    resetTable() {
        this.page = 1;
        this.previousPage = 1;
        this.loadAll();
    }

    onColumnsSelected(columns: ColumnModel[]) {
        this.hiddenColumns = this.getHiddenColumns(columns);
        this.resetTable();
    }

    private getHiddenColumns(selectedColumns): ColumnModel[] {
        const unselectedColumns: ColumnModel[] = selectedColumns.filter((column: ColumnModel) => !column.selected);
        const newHiddenColumns = [];
        unselectedColumns.forEach((column) => {
            newHiddenColumns[column.name] = true;
        });
        return newHiddenColumns;
    }

    get hiddenColumns() {
        return this.resultsHiddenColumns;
    }

    set hiddenColumns(hiddenColumns) {
        this.resultsHiddenColumns = hiddenColumns;
    }

    protected readonly MAX_NAME_DISPLAY_SIZE = MAX_NAME_DISPLAY_SIZE;
}

export enum VariableResultsSortEnum {
    'TEST_ENTRY' = 'TEST_ENTRY',
    'STD_ENTRY' = 'STD_ENTRY',
    'NO_ENV' = 'NO_ENV',
    'NO_SUP' = 'NO_SUP',
    'MEAN_TEST' = 'MEAN_TEST',
    'MEAN_STD' = 'MEAN_STD',
    'PVAL' = 'PVAL',
    'MEAN_DIFF' = 'MEAN_DIFF'
}
